/**
 * PredictiveEscalationAlerts Component
 * AI-powered predictions for patient escalation risk
 * Identifies stable patients likely to decline in next 48-72 hours
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { toast } from 'sonner';
import {
  Brain, AlertTriangle, TrendingUp, Activity, Clock,
  ChevronRight, Loader2, RefreshCw, Target, Shield,
  Calendar, CheckCircle2, Zap, ArrowRight, User,
  Sparkles, BarChart3, ClipboardList, PlusCircle, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PredictiveEscalationAlerts = ({ className = "", compact = false }) => {
  const [predictions, setPredictions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [modelInfo, setModelInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [predictionWindow, setPredictionWindow] = useState(72);
  const [expandedPatient, setExpandedPatient] = useState(null);
  const [generatingCarePlan, setGeneratingCarePlan] = useState(null);
  const [carePlanSuggestions, setCarePlanSuggestions] = useState({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem('hospice_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchPredictions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/scheduling/predictive-escalations?prediction_window=${predictionWindow}`,
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        setPredictions(data.predictions || []);
        setSummary(data.summary || {});
        setModelInfo(data.model_info || {});
      } else {
        toast.error('Failed to load predictions');
      }
    } catch (error) {
      console.error('Error fetching predictions:', error);
      toast.error('Error loading predictive alerts');
    } finally {
      setLoading(false);
    }
  }, [predictionWindow]);

  useEffect(() => {
    fetchPredictions();
  }, [fetchPredictions]);

  const generateCarePlanSuggestions = async (prediction) => {
    const patientId = prediction.patient_id;
    setGeneratingCarePlan(patientId);
    
    try {
      const response = await fetch(
        `${API_URL}/api/scheduling/generate-care-plan-suggestions`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            patient_id: patientId,
            risk_factors: prediction.risk_factors || [],
            current_acuity: prediction.current_acuity,
            predicted_acuity: prediction.predicted_acuity,
            diagnosis: prediction.diagnosis
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setCarePlanSuggestions(prev => ({
          ...prev,
          [patientId]: data.suggestions
        }));
        toast.success('Care plan suggestions generated', {
          description: `${data.suggestions?.length || 0} proactive interventions suggested`
        });
      } else {
        toast.error('Failed to generate care plan suggestions');
      }
    } catch (error) {
      console.error('Error generating care plan:', error);
      toast.error('Error generating suggestions');
    } finally {
      setGeneratingCarePlan(null);
    }
  };

  const applyCarePlanSuggestion = async (patientId, suggestion) => {
    try {
      const response = await fetch(
        `${API_URL}/api/scheduling/apply-care-plan-suggestion`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            patient_id: patientId,
            suggestion: suggestion
          })
        }
      );
      
      if (response.ok) {
        toast.success('Care plan updated', {
          description: suggestion.title
        });
        // Remove the applied suggestion from the list
        setCarePlanSuggestions(prev => ({
          ...prev,
          [patientId]: (prev[patientId] || []).filter(s => s.id !== suggestion.id)
        }));
      } else {
        toast.error('Failed to apply suggestion');
      }
    } catch (error) {
      console.error('Error applying suggestion:', error);
      toast.error('Error applying care plan update');
    }
  };

  const getProbabilityColor = (prob) => {
    if (prob >= 0.75) return 'text-red-600 bg-red-100 border-red-200';
    if (prob >= 0.5) return 'text-amber-600 bg-amber-100 border-amber-200';
    return 'text-emerald-600 bg-emerald-100 border-emerald-200';
  };

  const getProbabilityBg = (prob) => {
    if (prob >= 0.75) return 'bg-red-500';
    if (prob >= 0.5) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const getAcuityColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'moderate': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'low': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-amber-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <Card className={className} data-testid="predictive-escalation-loading">
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="relative">
              <Brain className="w-10 h-10 text-purple-600 animate-pulse" />
              <Sparkles className="w-4 h-4 text-purple-400 absolute -top-1 -right-1 animate-bounce" />
            </div>
            <span className="text-slate-600">AI analyzing patient data...</span>
            <Progress value={66} className="w-48 h-2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    // Compact view for embedding in other dashboards
    return (
      <Card className={`${className} border-2 border-purple-200/50`} data-testid="predictive-escalation-compact">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Brain className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-base">Predictive Escalation Alerts</CardTitle>
                <CardDescription className="text-xs">
                  AI-powered {predictionWindow}h risk predictions
                </CardDescription>
              </div>
            </div>
            {summary?.total_at_risk > 0 && (
              <Badge variant="destructive" className="gap-1">
                <AlertTriangle className="w-3 h-3" />
                {summary.total_at_risk} at risk
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {predictions.length === 0 ? (
            <div className="text-center py-4 text-slate-500">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
              <p className="text-sm font-medium">No escalation risks detected</p>
            </div>
          ) : (
            <div className="space-y-2">
              {predictions.slice(0, 3).map((pred, idx) => (
                <div
                  key={pred.patient_id || idx}
                  className="flex items-center justify-between p-2 bg-white border rounded-lg hover:shadow-sm transition-shadow"
                  data-testid={`prediction-compact-${idx}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getProbabilityColor(pred.escalation_probability)}`}>
                      <TrendingUp className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{pred.patient_name}</p>
                      <p className="text-xs text-slate-500">{Math.round(pred.escalation_probability * 100)}% risk</p>
                    </div>
                  </div>
                  <Badge className={`text-xs ${getAcuityColor(pred.predicted_level)}`}>
                    {pred.current_level} → {pred.predicted_level}
                  </Badge>
                </div>
              ))}
              {predictions.length > 3 && (
                <Button variant="ghost" size="sm" className="w-full text-purple-600 hover:text-purple-700">
                  View all {predictions.length} predictions <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full view
  return (
    <div className={className} data-testid="predictive-escalation-alerts">
      {/* Header */}
      <Card className="mb-6 border-2 border-purple-200/50 bg-gradient-to-r from-purple-50 to-indigo-50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Brain className="w-7 h-7 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-xl flex items-center gap-2">
                  Predictive Escalation Alerts
                  <Badge className="bg-purple-500/20 text-purple-700 border-0 text-xs">
                    <Sparkles className="w-3 h-3 mr-1" />
                    AI-Powered
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Predicting which stable patients may decline in the next {predictionWindow} hours
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={predictionWindow}
                onChange={(e) => setPredictionWindow(Number(e.target.value))}
                className="px-3 py-2 text-sm border rounded-lg bg-white"
                data-testid="prediction-window-select"
              >
                <option value={24}>Next 24 hours</option>
                <option value={48}>Next 48 hours</option>
                <option value={72}>Next 72 hours</option>
              </select>
              <Button variant="outline" size="sm" onClick={fetchPredictions} data-testid="refresh-predictions-btn">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">At Risk</p>
                <p className="text-2xl font-bold text-slate-900">{summary?.total_at_risk || 0}</p>
              </div>
              <div className="p-2 bg-purple-100 rounded-full">
                <AlertTriangle className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">High Risk</p>
                <p className="text-2xl font-bold text-red-700">{summary?.high_probability || 0}</p>
                <p className="text-xs text-red-500">≥75% probability</p>
              </div>
              <div className="p-2 bg-red-200 rounded-full">
                <Zap className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">Medium Risk</p>
                <p className="text-2xl font-bold text-amber-700">{summary?.medium_probability || 0}</p>
                <p className="text-xs text-amber-500">50-74% probability</p>
              </div>
              <div className="p-2 bg-amber-200 rounded-full">
                <Activity className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Avg Confidence</p>
                <p className="text-2xl font-bold text-slate-900">{Math.round((summary?.avg_confidence || 0) * 100)}%</p>
              </div>
              <div className="p-2 bg-indigo-100 rounded-full">
                <Target className="w-5 h-5 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Model Accuracy</p>
                <p className="text-2xl font-bold text-slate-900">{Math.round((modelInfo?.accuracy || 0) * 100)}%</p>
              </div>
              <div className="p-2 bg-emerald-100 rounded-full">
                <Shield className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Predictions List */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-500" />
            Patients at Risk of Escalation
          </CardTitle>
          <CardDescription>
            Click on a patient to see detailed risk factors and recommended actions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {predictions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-emerald-500" />
              <p className="text-lg font-medium">No Escalation Risks Detected</p>
              <p className="text-sm">All stable patients are predicted to remain stable in the next {predictionWindow} hours</p>
            </div>
          ) : (
            <div className="space-y-3">
              {predictions.map((pred, idx) => (
                <div
                  key={pred.patient_id || idx}
                  className={`border rounded-xl overflow-hidden transition-all ${
                    expandedPatient === pred.patient_id ? 'shadow-lg border-purple-300' : 'hover:shadow-md'
                  }`}
                  data-testid={`prediction-${idx}`}
                >
                  {/* Main Row */}
                  <div
                    className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpandedPatient(
                      expandedPatient === pred.patient_id ? null : pred.patient_id
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Probability Circle */}
                        <div className="relative">
                          <div className={`w-14 h-14 rounded-full flex items-center justify-center border-4 ${
                            pred.escalation_probability >= 0.75 ? 'border-red-500 bg-red-50' :
                            pred.escalation_probability >= 0.5 ? 'border-amber-500 bg-amber-50' :
                            'border-emerald-500 bg-emerald-50'
                          }`}>
                            <span className={`text-lg font-bold ${
                              pred.escalation_probability >= 0.75 ? 'text-red-600' :
                              pred.escalation_probability >= 0.5 ? 'text-amber-600' :
                              'text-emerald-600'
                            }`}>
                              {Math.round(pred.escalation_probability * 100)}%
                            </span>
                          </div>
                          <div className="absolute -bottom-1 -right-1 p-1 bg-white rounded-full shadow">
                            <Brain className="w-4 h-4 text-purple-500" />
                          </div>
                        </div>
                        
                        {/* Patient Info */}
                        <div>
                          <p className="font-semibold text-slate-900">{pred.patient_name}</p>
                          <p className="text-sm text-slate-500">{pred.diagnosis}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                            <span className="flex items-center gap-1">
                              <Activity className="w-3 h-3" />
                              PPS: {pred.pps_score}%
                            </span>
                            {pred.days_since_visit && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {pred.days_since_visit}d since visit
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Prediction Arrow */}
                      <div className="flex items-center gap-4">
                        <div className="text-center">
                          <p className="text-xs text-slate-400 mb-1">Current → Predicted</p>
                          <div className="flex items-center gap-2">
                            <Badge className={`${getAcuityColor(pred.current_level)} text-xs`}>
                              {pred.current_acuity}
                            </Badge>
                            <ArrowRight className="w-4 h-4 text-slate-400" />
                            <Badge className={`${getAcuityColor(pred.predicted_level)} text-xs`}>
                              {pred.predicted_acuity}
                            </Badge>
                          </div>
                        </div>
                        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${
                          expandedPatient === pred.patient_id ? 'rotate-90' : ''
                        }`} />
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedPatient === pred.patient_id && (
                    <div className="px-4 pb-4 border-t bg-slate-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                        {/* Risk Factors */}
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                            Risk Factors
                          </h4>
                          <div className="space-y-2">
                            {pred.risk_factors?.map((factor, fIdx) => (
                              <div key={fIdx} className="flex items-center justify-between p-2 bg-white rounded-lg border">
                                <span className="text-sm text-slate-600">{factor.description}</span>
                                <Badge variant="outline" className="text-xs">
                                  +{factor.contribution}%
                                </Badge>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Recommended Actions */}
                        <div>
                          <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            Recommended Actions
                          </h4>
                          <div className="space-y-2">
                            {pred.recommended_actions?.map((action, aIdx) => (
                              <div key={aIdx} className="flex items-start gap-2 p-2 bg-white rounded-lg border">
                                <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                                  action.priority === 'high' ? 'bg-red-500' :
                                  action.priority === 'medium' ? 'bg-amber-500' : 'bg-gray-400'
                                }`} />
                                <span className={`text-sm ${getPriorityColor(action.priority)}`}>
                                  {action.action}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      {/* Proactive Care Plan Suggestions */}
                      <div className="mt-4 pt-4 border-t">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                            <ClipboardList className="w-4 h-4 text-purple-500" />
                            Proactive Care Plan Suggestions
                            <Badge className="bg-purple-100 text-purple-700 border-0 text-xs">
                              <Sparkles className="w-3 h-3 mr-1" />
                              AI
                            </Badge>
                          </h4>
                          {!carePlanSuggestions[pred.patient_id] && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1 text-purple-600 border-purple-200 hover:bg-purple-50"
                              onClick={() => generateCarePlanSuggestions(pred)}
                              disabled={generatingCarePlan === pred.patient_id}
                              data-testid={`generate-care-plan-${pred.patient_id}`}
                            >
                              {generatingCarePlan === pred.patient_id ? (
                                <>
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Brain className="w-3 h-3" />
                                  Generate Suggestions
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                        
                        {carePlanSuggestions[pred.patient_id] ? (
                          <div className="space-y-2">
                            {carePlanSuggestions[pred.patient_id].map((suggestion, sIdx) => (
                              <div 
                                key={suggestion.id || sIdx} 
                                className="p-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Badge className={`text-xs ${
                                        suggestion.priority === 'high' ? 'bg-red-100 text-red-700' :
                                        suggestion.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                                        'bg-slate-100 text-slate-700'
                                      }`}>
                                        {suggestion.priority} priority
                                      </Badge>
                                      <Badge variant="outline" className="text-xs border-purple-300 text-purple-600">
                                        {suggestion.category}
                                      </Badge>
                                    </div>
                                    <p className="text-sm font-medium text-slate-800">{suggestion.title}</p>
                                    <p className="text-xs text-slate-600 mt-1">{suggestion.description}</p>
                                    {suggestion.rationale && (
                                      <p className="text-xs text-purple-600 mt-1 italic">
                                        <Brain className="w-3 h-3 inline mr-1" />
                                        {suggestion.rationale}
                                      </p>
                                    )}
                                  </div>
                                  <Button
                                    size="sm"
                                    className="gap-1 bg-purple-600 hover:bg-purple-700 text-white shrink-0"
                                    onClick={() => applyCarePlanSuggestion(pred.patient_id, suggestion)}
                                    data-testid={`apply-suggestion-${sIdx}`}
                                  >
                                    <PlusCircle className="w-3 h-3" />
                                    Apply
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-slate-500 bg-slate-50 rounded-lg border border-dashed">
                            <FileText className="w-6 h-6 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">Click "Generate Suggestions" to get AI-powered care plan recommendations</p>
                            <p className="text-xs mt-1 text-slate-400">Based on risk factors and predicted escalation</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                        <Button size="sm" className="gap-2 bg-purple-600 hover:bg-purple-700">
                          <Calendar className="w-4 h-4" />
                          Schedule Visit
                        </Button>
                        <Button variant="outline" size="sm" className="gap-2">
                          <User className="w-4 h-4" />
                          View Patient
                        </Button>
                        <div className="ml-auto text-xs text-slate-400">
                          Confidence: {Math.round(pred.confidence * 100)}% | {pred.prediction_timeframe}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Model Info Footer */}
      {modelInfo && (
        <Card className="mt-6 bg-slate-50">
          <CardContent className="py-3">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <Brain className="w-3 h-3" />
                  Model: {modelInfo.model_type}
                </span>
                <span>Accuracy: {Math.round(modelInfo.accuracy * 100)}%</span>
                <span>Precision: {Math.round(modelInfo.precision * 100)}%</span>
                <span>Recall: {Math.round(modelInfo.recall * 100)}%</span>
              </div>
              <span>Training data: {modelInfo.training_data}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PredictiveEscalationAlerts;
